import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Lock, CheckCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      toast({
        title: "Lien invalide",
        description: "Le lien de réinitialisation est invalide",
        variant: "destructive"
      });
      navigate('/login');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams, navigate, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Mot de passe trop court",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { authAPI } = await import('../../api');
      await authAPI.resetPassword(token, password);
      
      setSuccess(true);
      toast({
        title: "Mot de passe réinitialisé",
        description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe",
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6 py-32">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-[#A6A6A6]">GT</span>
            <span className="text-[#C00000]"> ENGINEERING</span>
          </h1>
          <p className="text-[#A6A6A6]">Engineered Performance.</p>
        </div>

        <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
          <CardHeader>
            <CardTitle className="text-white">Nouveau mot de passe</CardTitle>
            <CardDescription className="text-[#A6A6A6]">
              Choisissez un nouveau mot de passe sécurisé
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!success ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#A6A6A6]">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-[#A6A6A6]" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white pl-10 focus:border-[#C00000]"
                      required
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-[#A6A6A6]">Minimum 6 caractères</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#A6A6A6]">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-[#A6A6A6]" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white pl-10 focus:border-[#C00000]"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            ) : (
              <div className="text-center py-6">
                <div className="mb-4 inline-block p-4 bg-green-500/10 rounded-full">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Mot de passe réinitialisé !</h3>
                <p className="text-[#A6A6A6] mb-4">
                  Votre mot de passe a été changé avec succès.
                </p>
                <p className="text-[#A6A6A6] text-sm">
                  Redirection vers la page de connexion...
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
